import type { CellTone, PlanFocus } from '@features/workshop-plan/utils/plan-cell';

import { X, Search } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { Progress } from '@shared/components/ui/progress';
import { Separator } from '@shared/components/ui/separator';

import { TONE_ICONS, TONE_LABELS, TONE_STYLES } from '@features/workshop-plan/components/plan-tone';

/** Tones the operator can filter on, in the order of the workshop flow. */
const FILTER_TONES: Exclude<CellTone, 'other'>[] = ['waiting', 'inProgress', 'finished', 'empty'];

type Props = {
  total: number;
  occupied: number;
  counts: Record<Exclude<CellTone, 'other'>, number>;
  focus: PlanFocus;
  onFocusChange: (focus: PlanFocus) => void;
};

/**
 * Legend of the plan that is also its filter: one button per tone with its count, and a search on the order,
 * box or location. What does not match stays on the plan, faded, so the operator keeps his bearings.
 */
export const PlanToolbar = ({ total, occupied, counts, focus, onFocusChange }: Props) => (
  <div aria-live="polite" className="flex items-stretch gap-4 rounded-xl border border-border bg-card p-4">
    <div className="flex min-w-50 flex-col justify-center gap-1.5">
      <span className="text-xs text-muted-foreground">Occupation de l&apos;atelier</span>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold tabular-nums md:text-2xl">{occupied}</span>
        <span className="text-sm text-muted-foreground tabular-nums">/ {total} emplacements</span>
      </div>
      <Progress aria-label="Taux d'occupation" className="h-1.5" value={total === 0 ? 0 : (occupied / total) * 100} />
    </div>

    <Separator orientation="vertical" />

    <div aria-label="Filtrer par statut" className="flex flex-1 gap-2" role="group">
      {FILTER_TONES.map((tone) => {
        const Icon = TONE_ICONS[tone];
        const style = TONE_STYLES[tone];
        const active = focus.tone === tone;

        return (
          <button
            aria-pressed={active}
            className={cn(
              'flex min-h-16 max-w-50 flex-1 items-center justify-start gap-3 rounded-xl border border-l-4 bg-transparent px-3 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none',
              active ? [style.border, style.bgActive] : ['border-border', style.bgHover],
              // After the border color, otherwise tailwind-merge drops the colored left band
              style.band
            )}
            key={tone}
            onClick={() => onFocusChange({ ...focus, tone: active ? null : tone })}
            type="button"
          >
            <Icon aria-hidden className="size-4 shrink-0" />
            <span className="flex flex-col">
              <span className="text-lg leading-tight font-bold tabular-nums sm:text-[19px]">{counts[tone]}</span>
              <span className="text-xs text-muted-foreground">{TONE_LABELS[tone]}</span>
            </span>
          </button>
        );
      })}
    </div>

    <div className="relative w-75 self-center">
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        aria-label="Rechercher une commande, une caisse ou un emplacement"
        className="pr-10 pl-9"
        onChange={(event) => onFocusChange({ ...focus, query: event.target.value })}
        placeholder="Commande, caisse ou emplacement"
        size="sm"
        value={focus.query}
      />
      {focus.query !== '' && (
        <Button
          aria-label="Effacer la recherche"
          className="absolute top-1/2 right-1 -translate-y-1/2"
          onClick={() => onFocusChange({ ...focus, query: '' })}
          size="icon-sm"
          variant="ghost"
        >
          <X />
        </Button>
      )}
    </div>
  </div>
);
